import { useEffect, useRef } from "react";

/**
 * Loads the next page when a sentinel element scrolls into view.
 *
 * WHY THIS EXISTS — the mobile "transactions disappear when you scroll" bug.
 *
 * The grids previously paginated from `onScroll` + `onWheel` bound to the
 * table / .table-wrap element:
 *
 *     <table onScroll={tblScrollHandler} onWheel={tblScrollHandler}>
 *
 * That has two faults:
 *
 *   1. `.table-wrap` is `overflow-x: auto` only — it never scrolls
 *      VERTICALLY at any breakpoint. The page scrolls instead, so the
 *      element's own `scroll` event effectively never fires for vertical
 *      movement.
 *   2. `onWheel` masked fault 1 on desktop, because a mouse wheel fires over
 *      the element regardless of whether it scrolls. Touch devices emit no
 *      wheel event at all — so on mobile nothing ever triggered the next
 *      page, and the list simply appeared to end mid-scroll.
 *
 * An IntersectionObserver on a sentinel below the last row is agnostic to
 * WHICH ancestor scrolls (page, container, or both) and works identically
 * for touch, wheel, keyboard and programmatic scrolling.
 *
 * Usage:
 *     const sentinelRef = useInfiniteScroll(loadMore, hasMoreData, loading);
 *     ...
 *     <div ref={sentinelRef} aria-hidden="true" />
 *
 * @param {Function} onLoadMore  called when the sentinel becomes visible
 * @param {boolean}  hasMore     stop observing once there is nothing left
 * @param {boolean}  isLoading   suppress while a request is in flight
 */
export default function useInfiniteScroll(onLoadMore, hasMore, isLoading) {
  const sentinelRef = useRef(null);
  // keep the latest callback without re-creating the observer every render
  const cbRef = useRef(onLoadMore);
  cbRef.current = onLoadMore;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || isLoading) return undefined;

    // Older browsers (and jsdom in tests) may lack IntersectionObserver.
    if (typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0] && entries[0].isIntersecting) {
          cbRef.current();
        }
      },
      {
        // start loading slightly before the sentinel is on screen so the
        // next rows are usually there by the time the user reaches them
        rootMargin: "240px 0px",
        threshold: 0,
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isLoading]);

  return sentinelRef;
}
